import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';

import { AppIcon } from './AppIcon';
import { buildComponentMeta } from './meta';
import { useComponentMeta } from './registry';
import { imageCropMeta } from './imageCropMeta';
import { exportImageCrop, readCropImage } from './imageCrop';
import type { ImageCropKind } from './imageCrop';
import styles from './ImageCropDialog.module.css';

interface ImageCropDialogProps {
  file: File;
  kind: ImageCropKind;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
}

const PHONE_RATIOS = [
  { value: '16', label: '9:16' },
  { value: '18', label: '9:18' },
  { value: '19.5', label: '9:19.5' },
  { value: '20', label: '9:20' },
  { value: '21', label: '9:21' },
];

export function ImageCropDialog({ file, kind, onConfirm, onCancel }: ImageCropDialogProps): ReactElement {
  const [source, setSource] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [ratio, setRatio] = useState('19.5');
  const [customRatio, setCustomRatio] = useState(19.5);
  const [area, setArea] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  const overlayRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(imageCropMeta(kind, 'overlay')));
  const dialog = useComponentMeta<HTMLDivElement>(buildComponentMeta(imageCropMeta(kind, 'dialog')));
  const titleRef = useComponentMeta<HTMLHeadingElement>(buildComponentMeta(imageCropMeta(kind, 'title')));
  const closeRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(imageCropMeta(kind, 'close')));
  const ratioRef = useComponentMeta<HTMLSelectElement>(buildComponentMeta(imageCropMeta(kind, 'ratio')));
  const customRef = useComponentMeta<HTMLInputElement>(buildComponentMeta(imageCropMeta(kind, 'customRatio')));
  const viewportRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(imageCropMeta(kind, 'viewport')));
  const zoomRef = useComponentMeta<HTMLInputElement>(buildComponentMeta(imageCropMeta(kind, 'zoom')));
  const errorRef = useComponentMeta<HTMLParagraphElement>(buildComponentMeta(imageCropMeta(kind, 'error')));
  const resetRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(imageCropMeta(kind, 'reset')));
  const cancelButtonRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(imageCropMeta(kind, 'cancel')));
  const confirmRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(imageCropMeta(kind, 'confirm')));
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const titleId = useId();
  const isAvatar = kind === 'avatar';
  const aspect = isAvatar ? 1 : 9 / (ratio === 'custom' ? customRatio : Number(ratio));

  useEffect(() => {
    mounted.current = true;
    let active = true;
    void readCropImage(file, kind)
      .then((result) => { if (active) setSource(result); })
      .catch((failure: unknown) => { if (active) setError(failure instanceof Error ? failure.message : '读取图片失败'); });
    return () => { active = false; mounted.current = false; };
  }, [file, kind]);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelRef.current();
      }
      if (event.key !== 'Tab') return;
      const controls = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
      previousFocus?.focus();
    };
  }, [dialog]);

  async function confirm(): Promise<void> {
    if (!source || !area || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await exportImageCrop(source, area, kind);
      if (mounted.current) onConfirm(result);
    } catch (failure) {
      if (!mounted.current) return;
      setError(failure instanceof Error ? failure.message : '裁剪失败');
      setSaving(false);
    }
  }

  function reset(): void {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }

  return createPortal(
    <div ref={overlayRef} className={styles.overlay} data-ui-id={`imageCrop.${kind}.overlay`}>
      <div ref={dialog} className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className={styles.header}>
          <h2 ref={titleRef} id={titleId}>{isAvatar ? '裁剪头像' : '裁剪背景图片'}</h2>
          <button ref={closeRef} type="button" className={styles.iconButton} aria-label="取消裁剪" title="取消裁剪" onClick={onCancel}>
            <AppIcon name="close" size={20} />
          </button>
        </header>
        {!isAvatar ? (
          <div className={styles.ratios}>
            <label className={styles.selectRow}>竖屏比例
              <select ref={ratioRef} aria-label="竖屏比例" value={ratio} disabled={saving} onChange={(event) => { setRatio(event.currentTarget.value); reset(); }}>
                {PHONE_RATIOS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                <option value="custom">自定义</option>
              </select>
            </label>
            {ratio === 'custom' ? (
              <label className={styles.rangeRow}>自定义竖屏比例 <output>9:{customRatio.toFixed(1)}</output>
                <input ref={customRef} type="range" aria-label="自定义竖屏比例" min="12" max="24" step="0.1" value={customRatio} disabled={saving} onChange={(event) => setCustomRatio(Number(event.currentTarget.value))} />
              </label>
            ) : null}
          </div>
        ) : null}
        <div ref={viewportRef} className={styles.viewport} data-crop-shape={isAvatar ? 'round' : 'rect'}>
          {source ? (
            <Cropper
              image={source}
              crop={crop}
              zoom={zoom}
              minZoom={1}
              maxZoom={5}
              aspect={aspect}
              cropShape={isAvatar ? 'round' : 'rect'}
              showGrid={!isAvatar}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_percentage, pixels) => setArea(pixels)}
              onCropAreaChange={(_percentage, pixels) => setArea(pixels)}
              onMediaLoaded={() => setError(null)}
              mediaProps={{ onError: () => setError('无法解析图片，请选择其他图片') }}
              keyboardStep={10}
            />
          ) : !error ? <span role="status">正在加载图片…</span> : null}
        </div>
        <label className={styles.rangeRow}>缩放 <output>{zoom.toFixed(1)}x</output>
          <input ref={zoomRef} type="range" aria-label="裁剪缩放" min="1" max="5" step="0.01" value={zoom} disabled={!source || saving} onChange={(event) => setZoom(Number(event.currentTarget.value))} />
        </label>
        {error ? <p ref={errorRef} className={styles.error} role="alert">{error}</p> : null}
        <footer className={styles.actions}>
          <button ref={resetRef} type="button" className={styles.iconButton} aria-label="重置裁剪" title="重置裁剪" disabled={!source || saving} onClick={reset}><AppIcon name="refresh" size={20} /></button>
          <button ref={cancelButtonRef} type="button" onClick={onCancel}>取消</button>
          <button ref={confirmRef} type="button" className={styles.confirm} disabled={!area || saving} onClick={() => void confirm()}>{saving ? '处理中…' : '应用裁剪'}</button>
        </footer>
      </div>
    </div>, document.body,
  );
}
